import React, { useState, useEffect, useCallback } from 'react';
import { MSG } from "../../constants/toastMessages";
import AdminHeader from '../../layout/AdminHeader';
import noData from "../../assets/images/no-data.png";
import Loader from "../../assets/images/loader.gif";
import { apiCallingPost, prepareDownload } from '../../api/services';
import useDownloadLock from '../../hooks/useDownloadLock';
import startBrowserDownload from '../../utils/startBrowserDownload';
import Pagination from "react-js-pagination";
import { FaImage, FaVideo } from "react-icons/fa";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowRight } from "react-icons/md";
import MediaPreview from '../../components/MediaPreview';
import RequestSummaryTiles from '../../components/RequestSummaryTiles';
import ExpandableText from '../../components/ExpandableText';
import UsageChips from '../../components/UsageChips';
import DownloadMenu from '../../components/DownloadMenu';
import { toast } from 'react-toastify';
import { getDownloadFormatOptions, getGroupZipOptions, toDownloadItem, isDownloadableRequest } from '../../utils/downloadFormats';
import { groupRequestRows, formatFileSize, formatRequestDate, assetCountLabel, requestGroupTitle, statusBadgeClass, GROUP_STATUS_LABEL, ROW_STATUS_LABEL, statusTone } from '../../utils/requestGroups';
import '../../assets/css/admin-toolbar.css';

const LAST_SEEN_KEY = 'myRequestsLastSeen';

// Requester's grouped view (#9 minimal package): one card per request
// (submission), summary tiles, per-asset status + reason, per-request
// download of the approved assets, per-asset download.
function MyRequests() {
  const [groups, setGroups] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [tab, setTab] = useState('');
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [itemsCountPerPage, setItemsCountPerPage] = useState(10);
  const [expanded, setExpanded] = useState({});
  const [imagePopup, setImagePopup] = useState(false);
  const [popupSelectData, setPopupSelectData] = useState([]);
  // "NEW" marker: assets approved since the previous visit (per browser).
  const [lastSeen] = useState(() => {
    try { return Number(localStorage.getItem(LAST_SEEN_KEY)) || 0; } catch { return 0; }
  });

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') cancelPopup(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LAST_SEEN_KEY, String(Date.now())); } catch { /* per-browser convenience only */ }
  }, []);

  const fetchRequests = useCallback(async (page, perPage, status) => {
    setLoading(true);
    try {
      const response = await apiCallingPost("api/video/VideoDownloadRequestList", {
        grouped: true,
        max: Number(perPage),
        offset: (page - 1) * Number(perPage),
        status: status || "",
      });
      const { data } = response;
      if (data.success === "1") {
        const g = groupRequestRows(data.result);
        setGroups(g);
        setStatusCounts(data.statusCounts || {});
        setTotalCount(Number(data.totalCount) || 0);
        // first card open, the rest collapsed
        setExpanded(g.length ? { [g[0].groupKey]: true } : {});
      } else {
        setGroups([]);
        setStatusCounts({});
        setTotalCount(0);
        toast.error(data.errorMessage || MSG.couldNotProcess);
      }
    } catch {
      setGroups([]);
      setStatusCounts({});
      setTotalCount(0);
      toast.error(MSG.couldNotProcess);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests(activePage, itemsCountPerPage, tab);
  }, [fetchRequests, activePage, itemsCountPerPage, tab]);

  const changeTab = (next) => { setTab(next); setActivePage(1); };
  const changePerPage = (n) => { setItemsCountPerPage(n); setActivePage(1); };
  // Accordion: one request open at a time; clicking the open one closes it.
  const toggleGroup = (key) => setExpanded((prev) => (prev[key] ? {} : { [key]: true }));

  const popupTagShow = (data) => {
    setImagePopup(true);
    setPopupSelectData(data);
    document.body.style.overflow = 'hidden';
  };

  const cancelPopup = () => {
    setImagePopup(false);
    document.body.style.overflow = 'auto';
  };

  const { runLocked } = useDownloadLock();
  // Trigger the actual download for the given [{fileId, type, requestId}] items.
  // Locked per selection, so a double-click starts one download; a single
  // asset and a one-asset ZIP are different downloads, hence different keys.
  const runDownload = (items, label, key = `zip:${items.map((i) => i.fileId).join(',')}`) => runLocked(key, async () => {
    try {
      const { responseData } = await prepareDownload(items);
      startBrowserDownload(responseData, label || 'download');
      toast.success(MSG.downloadStarted);
    } catch (err) {
      toast.error(MSG.downloadFailed);
    }
  });

  // Single asset: the row's dropdown picks the variant; the item names the
  // request row so the server can authorise against exactly this request.
  const downloadVariant = (data, option) => {
    const item = toDownloadItem(data, option.type);
    return runDownload([item], data.videoName, `asset:${item.fileId}`);
  };

  // Whole request: every APPROVED asset of the group in one ZIP, in the
  // version the user picked from the header menu (getGroupZipOptions).
  const downloadGroupZip = (group, option) => {
    if (option.skipped > 0) toast.warning(MSG.zipSkippedNoFormat);
    runDownload(option.items, group.projectName || 'approved-media');
  };

  const isNew = (row) => row.status === "APPROVED" && Number(row.decidedOn) > lastSeen;

  return (
    <>
      <AdminHeader title="My Download Requests" />
      <div className='container mt-4'>
        <RequestSummaryTiles counts={statusCounts} value={tab} onChange={changeTab} />
        {loading ? (
          <div className="noData d-flex flex-column">
            <img src={Loader} alt="loading" className="img-fluid" width="150" height="150" />
          </div>
        ) : groups.length > 0 ? (
          <>
            <div className="request-groups">
              {groups.map((g) => {
                const open = !!expanded[g.groupKey];
                const zipOptions = getGroupZipOptions(g.rows);
                return (
                  <div className="request-group request-group--user" key={g.groupKey}>
                    <div className="request-group__header" role="button" tabIndex={0}
                      aria-expanded={open}
                      onClick={() => toggleGroup(g.groupKey)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(g.groupKey); } }}>
                      <span className="request-group__toggle">
                        {open ? <MdOutlineKeyboardArrowDown size={22} /> : <MdOutlineKeyboardArrowRight size={22} />}
                      </span>
                      <div className="request-group__main">
                        <span className="request-group__title" title={requestGroupTitle(g)}>{requestGroupTitle(g)}</span>
                        <UsageChips usageTypes={g.usageTypes} />
                        <ExpandableText text={g.projectBrief} limit={120} className="request-group__brief" />
                      </div>
                      {/* header cells share one grid with the asset rows below (see CSS):
                          date · count above type · size, the pill above each row's status,
                          the ZIP above each row's download */}
                      <div className="request-group__meta">
                        <span className="request-group__col request-group__col--date" title="Requested on">{formatRequestDate(g.groupRequestedDate)}</span>
                        <span className="request-group__col request-group__col--count">{assetCountLabel(g.assetCount)}</span>
                      </div>
                      <span className="request-group__status">
                        <span className={`status-badge ${statusBadgeClass(g.groupStatus)}`} title="Overall status of this request">
                          {GROUP_STATUS_LABEL[g.groupStatus] || g.groupStatus}
                        </span>
                      </span>
                      {/* stop the card header from toggling on the menu's click / Enter */}
                      <span className="request-group__action" role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        {zipOptions.length > 0
                          ? <DownloadMenu variant="zip" label="Download all approved assets as ZIP"
                              options={zipOptions} onPick={(o) => downloadGroupZip(g, o)} />
                          // Approved assets with nothing to offer would leave a
                          // bare gap next to an "Approved" request; a request
                          // with nothing approved yet is simply not actionable.
                          : g.rows.some((r) => r.status === "APPROVED")
                            ? <span className="text-muted" title="No downloadable version is available for the approved assets">—</span>
                            : null}
                      </span>
                    </div>
                    {open && (
                      <div className="request-group__body">
                        {g.rows.map((data) => {
                          const size = formatFileSize(data.fileSize);
                          // An approved asset can still have nothing to offer:
                          // older approvals granted one format, and some videos
                          // have no renditions. The menu renders nothing for an
                          // empty list, so say why instead of leaving a gap.
                          const formats = isDownloadableRequest(data) ? getDownloadFormatOptions(data) : [];
                          return (
                            <div className="request-row" key={data.requestVideoDownloadId}>
                              {/* the toggle column stays empty: assets sit indented under the title */}
                              <div className="request-row__main">
                                {/* A withdrawn file (rejected / deactivated after approval) comes back with
                                    every link blank, so there is nothing to preview; keep the slot for alignment. */}
                                {(data.videoUrl || (Array.isArray(data.qualityTypeArray) && data.qualityTypeArray.length > 0)) ? (
                                  <button type="button" className='video-list-preview-btn' title="Preview" onClick={() => popupTagShow(data)}>
                                    {String(data.fileTypeId) === "1" ? <FaImage size={12} /> : <FaVideo size={12} />}
                                  </button>
                                ) : (
                                  <span className='video-list-preview-btn' title="No preview: this file is no longer available" aria-hidden="true" style={{ opacity: 0.35, cursor: 'default' }}>
                                    {String(data.fileTypeId) === "1" ? <FaImage size={12} /> : <FaVideo size={12} />}
                                  </span>
                                )}
                                <div className="request-row__text">
                                  <div className='video-list-title-text'>
                                    {data.videoName}
                                    {isNew(data) && <span className="request-row__new" title="Approved since your last visit">NEW</span>}
                                  </div>
                                  {(data.eventName || data.video_location) && (
                                    <p className='small text-muted mb-0'>
                                      {data.eventName && <>Event: {data.eventName}</>}
                                      {data.eventName && data.video_location && <> · </>}
                                      {data.video_location && <>Location: {data.video_location}</>}
                                    </p>
                                  )}
                                  {data.status === "REJECTED" && data.rejectionReason && (
                                    <p className="request-group__reason">Reason: {data.rejectionReason}</p>
                                  )}
                                </div>
                              </div>
                              <span className="request-row__meta">
                                {String(data.fileTypeId) === "1" ? 'Image' : 'Video'}
                                {size && <> · {size}</>}
                              </span>
                              <span className="request-row__status">
                                <span className={`request-row__state request-row__state--${statusTone(data.status)}`}
                                  title={data.status === "OPEN" ? "Awaiting the approver's decision" : data.status === "APPROVED" ? "Approved — ready to download" : "Not approved"}>
                                  {ROW_STATUS_LABEL[data.status] || data.status}
                                </span>
                              </span>
                              <span className="request-row__action">
                                {formats.length > 0 ? (
                                  <DownloadMenu options={formats} onPick={(o) => downloadVariant(data, o)} />
                                ) : <span className="text-muted" title={
                                  data.status === "APPROVED" ? "Approved, but no downloadable version is available for this file"
                                    : data.status === "OPEN" ? "Available once approved" : "Not approved"
                                }>—</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end my-4 page-division">
              <div className="ms-auto d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  Requests per page:
                  <select value={itemsCountPerPage} style={{ minWidth: '60px' }}
                    onChange={(e) => changePerPage(Number(e.target.value))}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                {totalCount > itemsCountPerPage && (
                  <div className='pagination-wrapper m-0 p-0'>
                    <Pagination
                      activePage={activePage}
                      itemsCountPerPage={itemsCountPerPage}
                      totalItemsCount={totalCount}
                      pageRangeDisplayed={5}
                      onChange={(p) => setActivePage(p)}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="noData d-flex flex-column">
            <img src={noData} alt="noData" className="img-fluid" width="150" height="150" />
            <div>{tab ? `No ${GROUP_STATUS_LABEL[tab].toLowerCase()} requests` : 'No requests yet'}</div>
          </div>
        )}
        <MediaPreview
          open={imagePopup}
          url={popupSelectData.videoUrl}
          fileType={popupSelectData.fileTypeId}
          qualityTypeArray={popupSelectData.qualityTypeArray}
          poster={popupSelectData.thumbnail_image}
          autoPlay
          onClose={cancelPopup}
        />
      </div>
    </>
  );
}

export default MyRequests;
